import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { max, min } from '../../../theme/breakpoints';

export const SectionContainer = styled.div`
  position: relative;
`;

export const HeroContainer = styled(motion.div)`
  position: relative;
`;

type TOpacityFilter = {
  minHeight: number;
};
export const OpacityFilter = styled.div<TOpacityFilter>`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: ${({ minHeight }) => `${minHeight + 250}px`};
    background: linear-gradient(
      0.91deg,
      rgba(0, 0, 0, 0.7) 0.84%,
      rgba(0, 0, 0, 0.467003) 62.69%,
      rgba(0, 0, 0, 0) 103.01%
    );

    @media screen and ${min('tablet')} {
      width: 70%;
      height: 100%;
      background: linear-gradient(
        92.97deg,
        rgba(29, 29, 29, 0.7) 2.33%,
        rgba(29, 29, 29, 0.49) 67.46%,
        rgba(29, 29, 29, 0) 87.61%
      );
    }
    @media screen and ${min('largeDesktop')} {
      width: 55%;
    }
  }

  video {
    display: block;
    width: 100%;
    position: relative;
    height: ${({ minHeight }) => `${minHeight + 250}px`};
    object-fit: cover;
  }
`;

export const ForwardIconContainer = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  position: absolute;
  bottom: 0;
  left: 50%;
  padding: 0.625rem;
  cursor: pointer;
  display: none;

  @media screen and ${min('largeTablet')} {
    display: block;
  }
`;

export const AvailableOn = styled.div`
  background-color: black;
  color: white;
  padding: 0.5rem 1rem;
  margin: 1rem 0 0;
  white-space: nowrap;
  text-transform: uppercase;
  font-family: ${({ theme }) => theme.fonts.secondary.bold};

  @media screen and ${min('largeTablet')} {
    font-size: 0.85em;
    padding: 7px 13px;
  }
  @media screen and ${min('tablet')} {
    font-size: 0.75em;
    padding: 6px 13px;
    margin: 0 0 0 1.25rem;
  }
`;

interface IInnerContentProps {
  isFreeTrial: boolean | undefined;
  ref: any;
}

export const InnerContent = styled.div<IInnerContentProps>`
  position: absolute;
  width: 100%;
  bottom: 1rem;
  left: 0;
  padding: 1rem;

  @media screen and ${min('tablet')} {
    padding: 1rem 2.75rem;
  }
  @media screen and ${min('largeTablet')} {
    width: 75%;
    padding: 2.75rem;
  }
  @media screen and ${min('largeDesktop')} {
    width: 100%;
    max-width: 90rem;
    padding: 1rem;
    left: 50%;
    bottom: auto;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  > div {
    display: flex;
    flex-direction: column;

    @media screen and ${min('tablet')} {
      display: block;
    }
    ${({ isFreeTrial }) =>
      isFreeTrial
        ? css`
            background-color: rgba(35, 35, 35, 0.8);
            border-radius: 4px;
            border-width: 4px;
            border-style: solid;
            border-image: linear-gradient(180deg, #f8d224, #f34f66) 1;
            padding: 1.875rem 1.5rem;

            @media screen and ${min('tablet')} {
              padding: 1.875rem 3.125rem;
              max-width: 30.625rem;
            }
            @media screen and ${min('largeTablet')} {
              padding: 2.8125rem;
              max-width: 43.75rem;
            }
            @media screen and (min-width: 1525px) {
              padding: 4.375rem;
            }
          `
        : null};
  }

  h1 {
    color: ${({ theme }) => theme.colors.White};
    margin-bottom: 1.25rem;

    > span {
      ${({ isFreeTrial, theme }) =>
        isFreeTrial
          ? css`
              color: ${theme.colors.Carnation};
              background: -webkit-linear-gradient(#f46958, #f34f66);
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
              position: relative;
            `
          : css`
              color: ${theme.colors.RipeLemon};
            `};
    }

    &:not(.simple):after {
      display: none;
    }

    @media screen and ${max('mobile')} {
      width: 90%;
      margin: auto;
      margin-bottom: 1.25rem;
    }
  }

  p {
    text-align: center;
    color: white;
    margin-bottom: 1.25rem;
    font-size: 1rem;

    @media screen and ${min('tablet')} {
      margin-bottom: 2rem;
      text-align: left;
    }
    @media screen and ${min('largeTablet')} {
      font-size: 1.125rem;
      margin-bottom: 2.5rem;
    }
  }

  ${AvailableOn} {
    ${({ isFreeTrial }) =>
      isFreeTrial &&
      css`
        background-color: transparent;
        border: 1px solid ${({ theme }) => theme.colors.White};
      `};
  }
`;

export const RockSmithPlusContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 1rem 0 0;
  order: 1;

  @media screen and ${min('tablet')} {
    flex-direction: row;
    margin: 0 0 1rem;
  }

  svg {
    @media screen and ${max('tablet')} {
      width: 92px;
      height: 13px;
    }
  }
`;

export const RockSmithPlusImg = styled.img`
  @media screen and ${max('tablet')} {
    height: 13px;
  }
`;

export const Content = styled.div`
  max-width: 43.75rem;

  h1 {
    @media screen and ${min('tablet')} {
      text-align: left;
    }
  }
`;

export const LegalLineText = styled.p`
  font-size: 0.875rem !important;
  color: #a8a8a8 !important;
  margin-bottom: 0 !important;
  margin-top: 2.5rem;

  @media screen and ${max('tablet')} {
    font-size: 0.75rem !important;
    text-align: left !important;
  }
`;
export const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media screen and ${min('tablet')} {
    flex-direction: row;
  }

  a.outlined {
    color: ${({ theme }) => theme.colors.White};
    border-color: ${({ theme }) => theme.colors.White};
    margin-top: 1rem;

    @media screen and ${min('tablet')} {
      margin-top: 0rem;
      margin-left: 1rem;
    }
  }
`;

export const PickContainer = styled.div`
  position: absolute;
  top: -1rem;
  left: -1rem;
  display: none;
  text-align: center;

  @media screen and ${min('desktop')} {
    display: block;
  }

  svg {
    @media screen and ${max('largeTablet')} {
      height: 95px;
    }
  }
`;

export const FreeTrialText = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  text-transform: uppercase;
  font-family: ${({ theme }) => theme.fonts.secondary.bold};
  color: ${({ theme }) => theme.colors.White};
  font-size: 1.125rem;
  line-height: 1;
  transform: translate(-50%, -50%);
  margin-top: -0.625rem;

  @media screen and ${min('largeTablet')} {
    font-size: 1.5rem;
    margin-top: -1rem;
  }
`;

export const LegalContainer = styled.div`
  @media screen and ${min('largeTablet')} {
    position: absolute;
    top: 2rem;
    right: 2rem;
  }
`;
